import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useLocation, Link } from "react-router-dom";

import Loader from "../../common/loader";
import Crud from "../../common/crud";
import NotFound from "../../common/not-found";
import ViewTitle from "../../common/view-title";
import Trash from "../../common/trash";

import AuthContext from "../../context";
import { del, get, post, put } from "../../utils/api-services";
import helpers from "../../utils/helpers";

const useStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		paddingBottom: 20,
	},
	tableContainer: {
		width: "100%",
	},
}));

const Loaders = () => {
	const { showToast, signOut, showLoader } = useContext(AuthContext);
	const classes = useStyles();
	const { pathname } = useLocation();
	const [state, setState] = useState({
		loading: true,
		loaders: [],
		loader: {
			idff_loader: 0,
			name: "",
		},
		permissions: {},
	});

	useEffect(() => {
		window.document.title = "Loaders";

		validatePermissions();
	}, []);

	const validatePermissions = () => {
		const permissions = helpers.getPermissions(pathname);
		setState((prev) => ({ ...prev, permissions }));

		permissions.read_attr ? makeRequest() : setState((prev) => ({ ...prev, loading: false }));
	};

	const makeRequest = async () => {
		showLoader(true);
		const response = await get("/farmForest/loader");
		if (response.status === 200) {
			setState((prev) => ({ ...prev, loading: false, loaders: response?.data ?? [] }));
		} else {
			console.log("[Loaders] makeRequest error", response);
			helpers.failRequest(response, showToast, signOut);
		}
		showLoader(false);
	};

	const handleChange = ({ target }) => setState((prev) => ({ ...prev, loader: { ...prev.loader, [target.name]: target.value } }));

	const validateForm = (type) => {
		const { idff_loader, name } = state.loader;

		switch (type) {
			case "update":
				const data = state.loaders.filter((value) => value.idff_loader === idff_loader);

				if (data[0]) {
					const before = JSON.stringify(data[0]);
					const after = JSON.stringify(state.loader);
					if (before !== after) {
						if (name.trim()) {
							return true;
						} else {
							showToast("All fields are required", "error");
							return false;
						}
					} else {
						showToast("Update some field", "error");
						return false;
					}
				} else {
					showToast("Select some loader", "error");
					return false;
				}

			case "store":
				if (name.trim()) {
					return true;
				} else {
					showToast("All fields are required", "error");
					return false;
				}

			case "delete":
				if (idff_loader === 0) {
					showToast("Select some loader", "error");
					return false;
				}
				const cond = window.confirm("Are you sure you want to delete this loader?");

				return Boolean(idff_loader && cond);
			default:
				break;
		}
	};

	const clear = () => {
		setState((prev) => ({ ...prev, loader: { ...prev.loader, idff_loader: 0, name: "" } }));
	};

	const store = async () => {
		const cond = validateForm("store");

		if (cond) {
			showLoader(true);
			const response = await post("/farmForest/loader/store", state.loader);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, loaders: response?.data ?? prev.loaders, loader: { idff_loader: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Loaders] store error", response);
				helpers.failRequest(response, showToast, signOut);
				showLoader(false);

				return false;
			}
			showLoader(false);
		}

		return cond;
	};

	const update = async () => {
		const { idff_loader } = state.loader;
		const cond = validateForm("update");

		if (cond) {
			showLoader(true);
			const response = await put(`/farmForest/loader/${idff_loader}`, state.loader);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, loaders: response?.data ?? prev.loaders, loader: { idff_loader: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Loaders] update error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const destroy = async () => {
		const { idff_loader } = state.loader;
		const cond = validateForm("delete");

		if (cond) {
			showLoader(true);
			const response = await del(`/farmForest/loader/${idff_loader}`);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, loaders: response?.data ?? prev.loaders, loader: { idff_loader: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Loaders] destroy error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const renderCell = ({ row }) => {
		return (
			<Link to={`/history/ff_loader/${row.idff_loader}`} target="_blank">
				<Tooltip title="Show history" placement="top">
					<IconButton>
						<Icon>{"history"}</Icon>
					</IconButton>
				</Tooltip>
			</Link>
		);
	};

	const columns = [
		{ field: "name", headerName: "Name", flex: 1 },
		{ field: "history", headerName: "Actions", flex: 0.3, renderCell },
	];

	if (state.loading) return <Loader />;

	if (!state.permissions.read_attr) return <NotFound />;

	return (
		<div className={classes.root}>
			<ViewTitle sedeFlag title="Loaders" />
			<div>
				<Grid container>
					<Grid item xs={12} md={6}>
						<FormGroup>
							<FormLabel>{"Name"}</FormLabel>
							<FormControl variant="filled" component={Box} width="100%">
								<FilledInput autoComplete="off" type="text" placeholder="Name" name="name" value={state.loader.name} onChange={handleChange} />
							</FormControl>
						</FormGroup>
					</Grid>
				</Grid>
			</div>

			<Crud permissions={state.permissions} create={{ create: clear, store }} update={update} destroy={destroy} cancel={clear} />

			{state.permissions.delete_attr && <Trash table="ff_loader" id="idff_loader" onRefresh={makeRequest} />}

			<div className={classes.tableContainer}>
				<DataGrid
					rows={state.loaders}
					columns={columns}
					pageSize={45}
					density="compact"
					autoHeight
					getRowId={(row) => row.idff_loader}
					loading={state.loading}
					onRowClick={({ row }) => setState((prev) => ({ ...prev, loader: row }))}
				/>
			</div>
		</div>
	);
};

export default Loaders;
